import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Search, Clock, Users, BookOpen, Loader2, ArrowUpDown,
  ChevronLeft, ChevronRight, Sparkles, TrendingUp, Zap, GraduationCap,
  Heart, History, Home, MessageSquare, Timer,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { BACKEND_URL } from '@/config';
import { toast } from 'sonner';
import { useCategories } from '@/hooks/useCategories';

const API = `${BACKEND_URL}/api`;


// ── Recently Viewed (localStorage) ──
const RV_KEY = 'ci_recently_viewed';
const getRecentlyViewed = () => {
  try { return JSON.parse(localStorage.getItem(RV_KEY) || '[]'); } catch { return []; }
};

const BADGE_COLORS = {
  'NEW': { bg: '#8B5CF6', icon: Sparkles },
  'POPULAR': { bg: '#F59E0B', icon: TrendingUp },
  'FREE': { bg: '#02d1d4', icon: Zap },
  'CPD ACCREDITED': { bg: '#6366F1', icon: GraduationCap },
  'RECOMMENDED': { bg: '#10B981', icon: null },
  'DISCOUNTED': { bg: '#20acf6', icon: null },
  'COMPLIANCE': { bg: '#EF4444', icon: null },
  'STAFF PICK': { bg: '#0D9488', icon: null },
  'BEST SELLER': { bg: '#D97706', icon: null },
};

// ── Course Card ──
const CourseCard = ({ course, compact, wishlist, onToggleWishlist, isAuthenticated }) => {
  const isFree = course.is_free || course.price <= 0;
  const isWished = wishlist.includes(course.id);
  const badges = course.badges || [];
  const totalMins = course.total_duration_minutes || (course.duration_hours ? Math.round(course.duration_hours * 60) : 0);
  const durationLabel = totalMins > 0
    ? totalMins >= 60
      ? `${Math.round(totalMins / 60 * 10) / 10} hr${totalMins >= 120 ? 's' : ''}`
      : `${totalMins} min`
    : course.duration_hours > 0
      ? `${course.duration_hours} hr${course.duration_hours !== 1 ? 's' : ''}`
      : null;
  const discussionCount = course.discussion_count || 0;

  const handleWishlist = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onToggleWishlist(course.id);
  };

  return (
    <Link
      to={`/courses/${course.id}`}
      className={`block group ${compact ? 'min-w-[280px] max-w-[320px] snap-start shrink-0' : ''}`}
      data-testid={`course-card-${course.id}`}
    >
      <Card className="overflow-hidden border-slate-200/80 hover:shadow-lg hover:border-[#20acf6]/30 transition-all duration-200 h-full bg-white relative">
        <div className="aspect-[16/10] relative overflow-hidden bg-slate-100">
          {course.thumbnail_url ? (
            <img src={course.thumbnail_url} alt={course.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#20acf6]/10 to-[#02d1d4]/10">
              <BookOpen className="h-10 w-10 text-slate-300" />
            </div>
          )}
          {/* Badges - top left */}
          <div className="absolute top-2.5 left-2.5 flex flex-col gap-1">
            {badges.slice(0, 3).map((badge) => {
              const bc = BADGE_COLORS[badge] || { bg: '#64748B', icon: null };
              const Icon = bc.icon;
              return (
                <span key={badge} className="text-white text-[10px] font-bold px-2 py-0.5 rounded-md shadow-sm flex items-center gap-0.5" style={{ backgroundColor: bc.bg }} data-testid={`badge-${badge.toLowerCase().replace(/\s+/g, '-')}`}>
                  {Icon && <Icon className="h-2.5 w-2.5" />} {badge}
                </span>
              );
            })}
          </div>
          {/* Wishlist heart - top right */}
          {isAuthenticated && (
            <button
              onClick={handleWishlist}
              className={`absolute top-2.5 right-2.5 w-8 h-8 rounded-full flex items-center justify-center transition-all shadow-sm ${
                isWished ? 'bg-red-500 text-white' : 'bg-white/90 text-slate-400 hover:text-red-500 hover:bg-white'
              }`}
              data-testid={`wishlist-btn-${course.id}`}
              aria-label={isWished ? 'Remove from wishlist' : 'Add to wishlist'}
            >
              <Heart className={`h-4 w-4 ${isWished ? 'fill-current' : ''}`} />
            </button>
          )}
        </div>
        <CardContent className="p-4">
          {course.category && (
            <span className="text-[11px] font-semibold text-[#20acf6] uppercase tracking-wider">{course.category}</span>
          )}
          <h3 className="text-sm font-semibold text-[#2a3e5d] mt-1 mb-1.5 line-clamp-2 leading-snug group-hover:text-[#20acf6] transition-colors">
            {course.title}
          </h3>
          {!compact && course.short_description && (
            <p className="text-xs text-slate-500 line-clamp-2 mb-2">{course.short_description}</p>
          )}
          <div className="flex items-center justify-between mt-auto pt-2">
            <div className="flex items-center gap-3 text-[11px] text-slate-400">
              {durationLabel && (
                <span className="flex items-center gap-0.5" data-testid="duration-label"><Clock className="h-3 w-3" />{durationLabel}</span>
              )}
              <span className="flex items-center gap-0.5"><Users className="h-3 w-3" />{course.total_enrollments || 0}</span>
              {discussionCount > 0 && (
                <span className="flex items-center gap-0.5"><MessageSquare className="h-3 w-3" />{discussionCount}</span>
              )}
            </div>
            <span className={`text-sm font-bold ${isFree ? 'text-[#02d1d4]' : 'text-[#2a3e5d]'}`}>
              {isFree ? 'Free' : `£${(course.price || 0).toFixed(2)}`}
            </span>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
};

// ── Horizontal Carousel ──
const CourseCarousel = ({ title, icon: Icon, courses, accent, wishlist, onToggleWishlist, isAuthenticated }) => {
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const checkScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 10);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 10);
  }, []);

  useEffect(() => { checkScroll(); }, [courses, checkScroll]);

  const scroll = (dir) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * 340, behavior: 'smooth' });
    setTimeout(checkScroll, 400);
  };

  if (!courses || courses.length === 0) return null;

  return (
    <div className="relative" data-testid={`carousel-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${accent}15` }}>
            <Icon className="h-4 w-4" style={{ color: accent }} />
          </div>
          <h3 className="text-lg font-bold text-[#2a3e5d]">{title}</h3>
          <Badge variant="outline" className="text-[10px] text-slate-400">{courses.length}</Badge>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => scroll(-1)} disabled={!canScrollLeft} className="w-8 h-8 rounded-full border border-slate-200 flex items-center justify-center text-slate-400 hover:text-[#20acf6] hover:border-[#20acf6] disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button onClick={() => scroll(1)} disabled={!canScrollRight} className="w-8 h-8 rounded-full border border-slate-200 flex items-center justify-center text-slate-400 hover:text-[#20acf6] hover:border-[#20acf6] disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>
      <div ref={scrollRef} onScroll={checkScroll} className="flex gap-4 overflow-x-auto scroll-smooth snap-x snap-mandatory pb-2 -mx-1 px-1" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}>
        {courses.map(c => <CourseCard key={c.id} course={c} compact wishlist={wishlist} onToggleWishlist={onToggleWishlist} isAuthenticated={isAuthenticated} />)}
      </div>
    </div>
  );
};

// ── Main Page ──
const CoursesPage = () => {
  const CATEGORIES = useCategories();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAuthenticated, api } = useAuth();
  const [allCourses, setAllCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [sortBy, setSortBy] = useState('newest');
  const [priceFilter, setPriceFilter] = useState('all');
  const [levelFilter, setLevelFilter] = useState('all');
  const [durationFilter, setDurationFilter] = useState('all');
  const [courseType, setCourseType] = useState(searchParams.get('type') || 'staff');
  const [wishlist, setWishlist] = useState([]);
  const [recentlyViewedCourses, setRecentlyViewedCourses] = useState([]);

  const selectedCategory = searchParams.get('category') || 'all';
  const setSelectedCategory = (cat) => {
    const params = new URLSearchParams(searchParams);
    if (cat === 'all') { params.delete('category'); } else { params.set('category', cat); }
    setSearchParams(params, { replace: true });
  };

  // Fetch courses
  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        params.append('limit', '200');
        if (courseType) params.append('course_type', courseType);
        if (selectedCategory !== 'all') params.append('category', selectedCategory);
        if (searchTerm) params.append('search', searchTerm);
        if (levelFilter !== 'all') params.append('level', levelFilter);
        const res = await axios.get(`${API}/courses/public?${params}`);
        setAllCourses(res.data);
      } catch (err) {
        console.error('Failed to fetch courses:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [selectedCategory, searchTerm, levelFilter, courseType]);

  // Fetch wishlist
  useEffect(() => {
    if (isAuthenticated) {
      api.get('/wishlist').then(res => setWishlist(res.data || [])).catch(() => {});
    }
  }, [isAuthenticated, api]);

  // Recently viewed
  useEffect(() => {
    const rvIds = getRecentlyViewed();
    if (rvIds.length > 0 && allCourses.length > 0) {
      const rvCourses = rvIds.map(id => allCourses.find(c => c.id === id)).filter(Boolean).slice(0, 6);
      setRecentlyViewedCourses(rvCourses);
    }
  }, [allCourses]);

  const toggleWishlist = async (courseId) => {
    if (!isAuthenticated) { toast.error('Please log in to save courses'); return; }
    const isWished = wishlist.includes(courseId);
    try {
      if (isWished) {
        await api.delete(`/wishlist/${courseId}`);
        setWishlist(prev => prev.filter(id => id !== courseId));
        toast.success('Removed from wishlist');
      } else {
        await api.post(`/wishlist/${courseId}`);
        setWishlist(prev => [...prev, courseId]);
        toast.success('Added to wishlist');
      }
    } catch { toast.error('Failed to update wishlist'); }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams(searchParams);
    if (searchTerm) { params.set('search', searchTerm); } else { params.delete('search'); }
    setSearchParams(params, { replace: true });
  };

  // Price & duration filter + sort
  const filteredCourses = allCourses.filter(c => {
    if (priceFilter === 'free') { if (!(c.is_free || c.price <= 0)) return false; }
    else if (priceFilter === 'paid') { if (!(!c.is_free && c.price > 0)) return false; }
    else if (priceFilter === 'subscription') { if (!c.is_subscription_only) return false; }

    if (durationFilter !== 'all') {
      const mins = c.total_duration_minutes || (c.duration_hours ? Math.round(c.duration_hours * 60) : 0);
      if (durationFilter === 'under30' && mins >= 30) return false;
      if (durationFilter === '30to60' && (mins < 30 || mins > 60)) return false;
      if (durationFilter === '1to2h' && (mins < 60 || mins > 120)) return false;
      if (durationFilter === 'over2h' && mins <= 120) return false;
    }
    return true;
  });

  const sortedCourses = [...filteredCourses].sort((a, b) => {
    switch (sortBy) {
      case 'title_asc': return (a.title || '').localeCompare(b.title || '');
      case 'price_low': return (a.price || 0) - (b.price || 0);
      case 'popular': return (b.total_enrollments || 0) - (a.total_enrollments || 0);
      case 'newest':
      default: return (b.created_at || '').localeCompare(a.created_at || '');
    }
  });

  // Carousel data
  const [carouselCourses, setCarouselCourses] = useState([]);
  useEffect(() => {
    if (selectedCategory === 'all' && !searchTerm && levelFilter === 'all') {
      setCarouselCourses(allCourses);
    } else if (carouselCourses.length === 0) {
      axios.get(`${API}/courses/public?limit=200`).then(r => setCarouselCourses(r.data)).catch(() => {});
    }
  }, [allCourses, selectedCategory, searchTerm, levelFilter]);

  const freeCourses = carouselCourses.filter(c => c.is_free || c.price <= 0);
  const popularCourses = [...carouselCourses].sort((a, b) => (b.total_enrollments || 0) - (a.total_enrollments || 0)).slice(0, 12);
  const newCourses = [...carouselCourses].sort((a, b) => (b.created_at || '').localeCompare(a.created_at || '')).slice(0, 12);
  const quickLearnCourses = carouselCourses.filter(c => {
    const mins = c.total_duration_minutes || (c.duration_hours ? Math.round(c.duration_hours * 60) : 0);
    return mins > 0 && mins <= 30;
  });

  const showCarousels = selectedCategory === 'all' && !searchTerm && priceFilter === 'all' && levelFilter === 'all';

  return (
    <div className="min-h-screen flex flex-col bg-[#F8FAFC]" data-testid="courses-page">
      <Helmet>
        <title>Courses | Common Impact E-Learning</title>
        <meta name="description" content="Unlock career growth with free CPD training courses for care workers. Browse courses in safeguarding, mental health, leadership, compliance, and more." />
      </Helmet>
      <Header />

      {/* ── Hero ── */}
      <section className="relative bg-[#2a3e5d] overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#2a3e5d] via-[#2a3e5d] to-[#20acf6]/20" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20 text-center">
          <Badge className="bg-white/10 text-white/90 border-white/20 mb-4 text-xs tracking-wide">
            <GraduationCap className="h-3 w-3 mr-1" /> CARE SECTOR TRAINING
          </Badge>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4 leading-tight" data-testid="courses-hero-title">
            Unlock Career Growth with{' '}
            <span className="text-[#02d1d4]">Free CPD Training</span>
          </h1>
          <p className="text-base text-white/70 max-w-2xl mx-auto mb-8">
            Build your skills with accredited courses designed for care workers, support staff,
            and organisations in supported living, care homes, and homeless services.
          </p>
          <form onSubmit={handleSearch} className="max-w-2xl mx-auto">
            <div className="relative group">
              <Search className="absolute left-5 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400 group-focus-within:text-[#02d1d4] transition-colors" />
              <Input
                type="text"
                placeholder="Search courses by topic, skill, or keyword..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-14 pr-28 h-14 text-base font-medium bg-white text-[#2a3e5d] border-2 border-white/20 rounded-2xl shadow-xl shadow-black/10 placeholder:text-slate-400 placeholder:font-medium hover:border-white/40 focus:border-[#02d1d4] focus:ring-2 focus:ring-[#02d1d4]/30 focus:shadow-2xl focus:shadow-[#02d1d4]/10 transition-all duration-200"
                data-testid="course-search"
              />
              <Button type="submit" className="absolute right-2 top-1/2 -translate-y-1/2 h-10 px-6 bg-[#20acf6] hover:bg-[#3bb8f7] rounded-xl font-semibold shadow-lg shadow-[#20acf6]/20 transition-all">
                Search
              </Button>
            </div>
          </form>
        </div>
      </section>

      {/* ── Course Type Toggle ── */}
      <section className="pt-6 pb-2" data-testid="course-type-toggle">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 p-1 bg-slate-100 rounded-xl w-fit">
            <button
              onClick={() => { setCourseType('staff'); setSearchParams(p => { p.set('type', 'staff'); return p; }, { replace: true }); }}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                courseType === 'staff' ? 'bg-[#2a3e5d] text-white shadow-md' : 'text-slate-600 hover:text-[#2a3e5d]'
              }`}
              data-testid="tab-staff-courses"
            >
              <GraduationCap className="h-4 w-4" /> Staff Training
            </button>
            <button
              onClick={() => { setCourseType('resident'); setSearchParams(p => { p.set('type', 'resident'); return p; }, { replace: true }); }}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                courseType === 'resident' ? 'bg-[#10B981] text-white shadow-md' : 'text-slate-600 hover:text-[#10B981]'
              }`}
              data-testid="tab-resident-courses"
            >
              <Home className="h-4 w-4" /> Resident Courses
            </button>
          </div>
          {courseType === 'resident' && (
            <p className="mt-3 text-sm text-[#10B981] font-medium">
              Free courses designed to help residents build independence, digital skills, and workplace readiness.
            </p>
          )}
        </div>
      </section>

      {/* ── Category Browse ── */}
      <section className="py-8 border-b border-slate-100" data-testid="category-browse">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-lg font-bold text-[#2a3e5d]">Browse by Category</h2>
            <span className="text-sm text-slate-500" data-testid="total-course-count">
              {loading ? '...' : `${allCourses.length} course${allCourses.length !== 1 ? 's' : ''} available`}
            </span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
            <button
              onClick={() => setSelectedCategory('all')}
              className={`rounded-xl p-3.5 text-center text-sm font-semibold transition-all duration-200 border-2 ${
                selectedCategory === 'all'
                  ? 'border-[#20acf6] bg-[#20acf6] text-white shadow-lg shadow-[#20acf6]/20'
                  : 'border-slate-200 bg-white text-[#2a3e5d] hover:border-[#20acf6]/40 hover:shadow-md'
              }`}
              data-testid="category-all"
            >
              All Courses
            </button>
            {CATEGORIES.map(cat => {
              const isActive = selectedCategory === cat.name;
              return (
                <button
                  key={cat.name}
                  onClick={() => setSelectedCategory(cat.name)}
                  className={`rounded-xl p-3.5 text-center text-sm font-semibold transition-all duration-200 border-2 ${
                    isActive ? 'text-white shadow-lg' : 'border-slate-200 bg-white text-[#2a3e5d] hover:shadow-md'
                  }`}
                  style={isActive ? { borderColor: cat.accent, backgroundColor: cat.accent, boxShadow: `0 4px 14px ${cat.accent}30` } : undefined}
                  onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.borderColor = cat.accent + '60'; }}
                  onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.borderColor = ''; }}
                  data-testid={`category-${cat.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                >
                  {cat.name}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── Filters + Course Grid ── */}
      <section className="py-8 flex-1">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Filter bar */}
          <div className="flex flex-wrap items-center gap-3 mb-6">
            {selectedCategory !== 'all' && (
              <div className="flex items-center gap-1.5">
                <Badge className="bg-[#20acf6]/10 text-[#20acf6] border-[#20acf6]/20 text-xs">{selectedCategory}</Badge>
                <button onClick={() => setSelectedCategory('all')} className="text-xs text-slate-400 hover:text-red-500">Clear</button>
              </div>
            )}

            {/* Price filter */}
            <Select value={priceFilter} onValueChange={setPriceFilter}>
              <SelectTrigger className="w-36 border-slate-200 text-sm h-9" data-testid="filter-price">
                <SelectValue placeholder="Price" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Prices</SelectItem>
                <SelectItem value="free">Free</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
                <SelectItem value="subscription">Subscription</SelectItem>
              </SelectContent>
            </Select>

            {/* Level filter */}
            <Select value={levelFilter} onValueChange={setLevelFilter}>
              <SelectTrigger className="w-36 border-slate-200 text-sm h-9" data-testid="filter-level">
                <SelectValue placeholder="Level" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Levels</SelectItem>
                <SelectItem value="beginner">Beginner</SelectItem>
                <SelectItem value="intermediate">Intermediate</SelectItem>
                <SelectItem value="advanced">Advanced</SelectItem>
              </SelectContent>
            </Select>

            {/* Duration filter */}
            <Select value={durationFilter} onValueChange={setDurationFilter}>
              <SelectTrigger className="w-40 border-slate-200 text-sm h-9" data-testid="filter-duration">
                <Timer className="h-3 w-3 mr-1" />
                <SelectValue placeholder="Duration" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Durations</SelectItem>
                <SelectItem value="under30">Under 30 min</SelectItem>
                <SelectItem value="30to60">30-60 min</SelectItem>
                <SelectItem value="1to2h">1-2 hours</SelectItem>
                <SelectItem value="over2h">2+ hours</SelectItem>
              </SelectContent>
            </Select>

            {/* Sort */}
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-40 border-slate-200 text-sm h-9" data-testid="sort-courses">
                <ArrowUpDown className="h-3 w-3 mr-1" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="newest">Newest</SelectItem>
                <SelectItem value="popular">Most Popular</SelectItem>
                <SelectItem value="title_asc">A — Z</SelectItem>
                <SelectItem value="price_low">Price: Low to High</SelectItem>
              </SelectContent>
            </Select>

            <span className="ml-auto text-sm text-slate-500">
              {loading ? '...' : `${sortedCourses.length} result${sortedCourses.length !== 1 ? 's' : ''}`}
            </span>
          </div>

          {loading ? (
            <div className="flex justify-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-[#20acf6]" />
            </div>
          ) : sortedCourses.length === 0 ? (
            <div className="text-center py-20">
              <BookOpen className="h-14 w-14 text-slate-300 mx-auto mb-3" />
              <h3 className="text-lg font-semibold text-[#2a3e5d] mb-1">No courses found</h3>
              <p className="text-sm text-slate-500">Try a different category, filter, or search term.</p>
              <Button variant="outline" className="mt-4" onClick={() => { setSelectedCategory('all'); setSearchTerm(''); setPriceFilter('all'); setLevelFilter('all'); setDurationFilter('all'); }}>
                View All Courses
              </Button>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
              {sortedCourses.map(c => (
                <CourseCard key={c.id} course={c} wishlist={wishlist} onToggleWishlist={toggleWishlist} isAuthenticated={isAuthenticated} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ── Recently Viewed ── */}
      {isAuthenticated && recentlyViewedCourses.length > 0 && (
        <section className="py-8 bg-slate-50 border-t border-slate-100" data-testid="recently-viewed-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <CourseCarousel title="Recently Viewed" icon={History} courses={recentlyViewedCourses} accent="#64748B" wishlist={wishlist} onToggleWishlist={toggleWishlist} isAuthenticated={isAuthenticated} />
          </div>
        </section>
      )}

      {/* ── Recommended Carousels ── */}
      {showCarousels && (
        <section className="py-12 bg-white border-t border-slate-100" data-testid="recommended-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
            <div className="text-center mb-2">
              <h2 className="text-xl font-bold text-[#2a3e5d]">Recommended for You</h2>
              <p className="text-sm text-slate-500 mt-1">Discover courses to advance your career in care</p>
            </div>
            <CourseCarousel title="Free Courses" icon={Zap} courses={freeCourses} accent="#02d1d4" wishlist={wishlist} onToggleWishlist={toggleWishlist} isAuthenticated={isAuthenticated} />
            {quickLearnCourses.length > 0 && (
              <CourseCarousel title="Quick Learn (Under 30 min)" icon={Timer} courses={quickLearnCourses} accent="#F59E0B" wishlist={wishlist} onToggleWishlist={toggleWishlist} isAuthenticated={isAuthenticated} />
            )}
            <CourseCarousel title="Popular Courses" icon={TrendingUp} courses={popularCourses} accent="#20acf6" wishlist={wishlist} onToggleWishlist={toggleWishlist} isAuthenticated={isAuthenticated} />
            <CourseCarousel title="New Courses" icon={Sparkles} courses={newCourses} accent="#8B5CF6" wishlist={wishlist} onToggleWishlist={toggleWishlist} isAuthenticated={isAuthenticated} />
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
};

export default CoursesPage;
